import {
  AGENT_NAME,
  activeOrder,
  deliveryLine,
  pendingOrder,
  activeSupplements,
  partOrder,
  partsToOrder,
  rfqParts,
  cmpTsv,
  daysAgo,
  fmtNum,
  offerQuestions,
  poMoney,
  poTotals,
  rfqState,
  roDateTime,
  roDate,
  siteLabel,
  status,
  type Rfq,
  LINK_CHANNEL,
  t,
  translateLoose,
} from '@procumi/shared';
import { useEffect, useState } from 'react';
import { AskDialog } from '../components/AskDialog';
import { ChoiceDialog } from '../components/ChoiceDialog';
import { ComparisonTable } from '../components/ComparisonTable';
import { FollowUpDialog } from '../components/FollowUpDialog';
import { MailDialog } from '../components/MailDialog';
import { OfferDialog } from '../components/OfferDialog';
import { OrderDialog } from '../components/OrderDialog';
import { SplitAward } from '../components/SplitAward';
import { SplitDialog } from '../components/SplitDialog';
import { PlansSection } from '../components/Plans';
import { Avatar, SupplierStack } from '../components/Avatar';
import { MoreMenu } from '../components/MoreMenu';
import { Comments } from '../components/Comments';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { copyText } from '../lib/clipboard';
import type { Route } from '../routes';
import { ask as askUser } from '../lib/confirm';

type OpenDialog =
  | { kind: 'mail'; sendId?: string }
  | { kind: 'choice' }
  /** `inboxId`: oferta venită pe mail, cu prețurile citite de agent */
  | { kind: 'offer'; sendId: string; inboxId?: string }
  | { kind: 'followUp'; sendId: string }
  /** `sendId`: întrebarea pentru acel furnizor (transportul necunoscut); lipsă = câștigătorul */
  | { kind: 'ask'; sendId?: string }
  /** `supplierId`: partea comenzii împărțite; lipsă = furnizorul ales */
  | { kind: 'order'; supplierId?: string }
  | { kind: 'supplement'; supplierId?: string }
  | { kind: 'split' }
  | null;

export function RfqDetailPage({
  id,
  go,
  toast,
  signature,
  canExtract,
  openOrder = false,
  followUp,
  inbox,
}: {
  id: string;
  /** mailul din căsuța agentului a cărui ofertă se deschide de la început */
  inbox?: string;
  /** deschide dialogul comenzii de la început (din „De comandat”) */
  openOrder?: boolean;
  /** trimiterea al cărei follow-up se deschide de la început (din mailul de dimineață) */
  followUp?: string;
  go: (r: Route) => void;
  toast: Toast;
  signature: string;
  /** citirea automată a ofertelor e pornită pe server */
  canExtract: boolean;
}) {
  const { data: rfq, setData, error, loading, reload } = useAsync(() => api.rfq(id), [id], `rfq:${id}`);
  // Reperul din istoric e un plus: dacă nu vine, comparația merge și fără el.
  // Cererea însăși nu intră în istoric, deci ofertele noi nu-l schimbă.
  const bench = useAsync(() => api.bench(id), [id], `bench:${id}`);
  // ofertele aduse de agent pe mail, citite, care așteaptă să fie verificate
  const mails = useAsync(() => api.inbox(id), [id], `inbox:${id}`);
  const [dialog, setDialog] = useState<OpenDialog>(
    openOrder ? { kind: 'order' } : followUp ? { kind: 'followUp', sendId: followUp } : null,
  );
  const [busy, setBusy] = useState(false);
  // din căsuța agentului („Verifică oferta”): formularul pornește cu prețurile citite
  useEffect(() => {
    const m = inbox ? mails.data?.mails.find((x) => x.id === inbox) : undefined;
    if (m?.sendId) setDialog({ kind: 'offer', sendId: m.sendId, inboxId: m.id });
  }, [inbox, mails.data]);

  const back =
    rfq?.kind === 'reorder' ? (
      <button className="back" onClick={() => go({ view: 'reorder' })}>
        {t('‹ Comandă rapidă')}
      </button>
    ) : (
      <button className="back" onClick={() => go({ view: 'rfqs' })}>
        {t('‹ Toate cererile')}
      </button>
    );

  if (loading) {
    return (
      <>
        {back}
        <Loading />
      </>
    );
  }
  if (error || !rfq) {
    return (
      <>
        {back}
        <ErrorBox error={error ?? t('Cererea nu a fost găsită.')} onRetry={reload} />
      </>
    );
  }

  const got = rfq.sends.filter((s) => s.offer).length;
  const unsent = rfq.sends.filter((s) => !s.sentAt && !s.offer).length;
  const openSend = dialog && 'sendId' in dialog ? rfq.sends.find((s) => s.id === dialog.sendId) : undefined;
  // comanda în vigoare și cele anulate, pentru bara furnizorului ales
  const order = activeOrder(rfq);
  const waiting = order ? null : pendingOrder(rfq);
  const supplements = activeSupplements(rfq);
  const canceledOrders = (rfq.orders ?? []).filter(
    (o) => (o.status === 'canceled' || o.status === 'rejected') && o.kind !== 'supplement',
  );
  const lastCanceled = order || waiting ? null : (canceledOrders[0] ?? null);
  // ce lipsește din oferta care ar câștiga; dialogul o ia de aici, ca să nu rămână pe date vechi
  const ask = offerQuestions(rfq, dialog?.kind === 'ask' ? dialog.sendId : undefined);
  const askSend = ask ? rfq.sends.find((s) => s.id === ask.sendId) : undefined;
  const onUpdated = (updated: Rfq) => setData(updated);
  // Furnizorul comenzii din dialog: cel cerut sau, deschis din panou („Fă comanda”),
  // prima parte încă necomandată; la un singur furnizor, cel ales.
  const orderFor =
    dialog?.kind === 'order'
      ? (dialog.supplierId ?? partsToOrder(rfq)[0]?.supplier.id ?? rfq.chosen?.supplier.id)
      : undefined;

  const stare = rfqState(rfq);

  const keepPrices = async () => {
    setBusy(true);
    try {
      const out = await api.keepPrices(rfq.id);
      toast(
        t(
          '{0} {1} până la {2}. Le găsești la Comandă rapidă.',
          out.saved,
          out.saved === 1 ? t('preț păstrat') : t('prețuri păstrate'),
          roDate(out.validUntil),
        ),
      );
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleClosed = async () => {
    setBusy(true);
    try {
      setData(stare === 'open' ? await api.closeRfq(rfq.id) : await api.reopenRfq(rfq.id));
      toast(stare === 'open' ? t('Cerere închisă') : t('Cerere redeschisă'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Anularea șterge și furnizorul ales: dacă s-a renunțat, nu s-a cumpărat
   * nimic, iar totalul șantierului n-are voie să pretindă altceva. De asta
   * întrebăm întâi, în loc să o facem pe tăcute.
   */
  const cancel = async () => {
    const pierdut = rfq.chosen
      ? rfq.chosen.split?.length
        ? t(
            ' Se pierde și împărțirea între {0}, fiindcă o cerere anulată nu are comandă.',
            rfq.chosen.split.map((p) => p.supplier.name).join(' și '),
          )
        : t(
            ' Se pierde și furnizorul ales ({0}), fiindcă o cerere anulată nu are comandă.',
            rfq.chosen.supplier.name,
          )
      : '';
    const ok = await askUser({
      title: t('Anulezi cererea?'),
      message: t('„{0}” trece la anulate.{1} Ofertele primite rămân.', rfq.title, pierdut),
      confirm: t('Anulează cererea'),
      cancel: t('Nu anula'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      setData(await api.cancelRfq(rfq.id));
      toast(t('Cerere anulată'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Ștergerea duce cu ea ofertele și prețurile. Spunem exact ce se pierde și
   * propunem închiderea, care e aproape mereu ce vrea omul.
   */
  const remove = async () => {
    const docs = rfq.sends.reduce((n, s) => n + s.files.length, 0);
    const alsoDocs = docs
      ? t(' Se șterg și {0} {1}.', docs, docs === 1 ? t('documentul primit') : t('documentele primite'))
      : '';
    const message = got
      ? t(
          'Se șterg cererea și {0} {1}, cu tot cu prețurile din ele.{2} Dacă vrei doar să nu-ți mai stea în listă, închide cererea.',
          got,
          got === 1 ? t('oferta primită') : t('oferte primite'),
          alsoDocs,
        )
      : t('„{0}” nu a primit nicio ofertă.{1}', rfq.title, alsoDocs || t(' Nu se pierde nimic.'));
    const ok = await askUser({
      title: got ? t('Ștergi definitiv cererea?') : t('Ștergi cererea?'),
      message,
      confirm: t('Șterge cererea'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const result = await api.deleteRfq(rfq.id);
      toast(
        result.offers
          ? t(
              'Cerere ștearsă, împreună cu {0} {1}',
              result.offers,
              result.offers === 1 ? t('ofertă') : t('oferte'),
            )
          : t('Cerere ștearsă'),
      );
      go({ view: 'site', id: rfq.site.id });
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <>
      {back}
      <div className="head rfq-head">
        <div>
          <h1>
            {rfq.title}
            {stare === 'open' ? null : (
              <span className="pill off" style={{ marginLeft: 10, verticalAlign: 'middle' }}>
                {stare === 'canceled' ? t('Anulată') : t('Închisă')}
              </span>
            )}
          </h1>
          <div className="meta-chips">
            <span className="mchip">{rfq.category}</span>
            <button className="mchip link-chip" onClick={() => go({ view: 'site', id: rfq.site.id })}>
              <i className="dot" />
              {siteLabel(rfq.site)}
            </button>
            {rfq.site.address ? <span className="mchip">{t('Livrare: {0}', deliveryLine(rfq.site))}</span> : null}
            <span className="mchip">{t('Termen răspuns: {0}', roDate(rfq.deadline))}</span>
            {rfq.neededBy ? (
              <span className="mchip">
                {t('Pe șantier')}: {roDate(rfq.neededBy)}
              </span>
            ) : null}
            <span className="mchip">
              <SupplierStack
                suppliers={rfq.sends.map((s) => ({
                  id: s.id,
                  name: s.supplier.name,
                  answered: !!s.offer,
                }))}
              />
            </span>
            {rfq.createdBy ? (
              <span className="mchip">
                <Avatar name={rfq.createdBy.name} />
                {rfq.createdBy.name}
              </span>
            ) : null}
          </div>
        </div>
        <div className="acts">
          {rfq.kind === 'reorder' ? null : (
            <button className="btn" onClick={() => setDialog({ kind: 'mail' })}>
              {t('Mailurile')}
            </button>
          )}
          {stare === 'canceled' || rfq.kind === 'reorder' ? null : (
            <button
              className={`btn${rfq.chosen ? '' : ' primary'}`}
              onClick={() => setDialog({ kind: 'choice' })}
            >
              {rfq.chosen ? t('Schimbă furnizorul') : t('Am ales furnizorul')}
            </button>
          )}
          <MoreMenu
            busy={busy}
            items={[
              {
                label: stare === 'open' ? t('Închide cererea') : t('Redeschide'),
                run: () => void toggleClosed(),
              },
              ...(stare === 'open' ? [{ label: t('Anulează cererea'), run: () => void cancel() }] : []),
              { label: t('Șterge cererea'), run: () => void remove(), danger: true },
            ]}
          />
        </div>
      </div>

      {(mails.data?.mails ?? [])
        .filter((m) => m.sendId && rfq.sends.some((x) => x.id === m.sendId))
        .map((m) => {
          const sup = rfq.sends.find((x) => x.id === m.sendId)!.supplier.name;
          return (
            <div key={m.id} className="ag-banner" role="status">
              <p>
                <b>{t('{0} a primit oferta de la {1} pe mail', AGENT_NAME, sup)}</b>
                {m.readCount
                  ? t(' și a citit {0} {1}', m.readCount, m.readCount === 1 ? t('poziție') : t('poziții'))
                  : ''}
                .{m.note ? ` ${translateLoose(m.note)}` : t(' Verific-o și salveaz-o.')}
              </p>
              <button
                className="btn primary small"
                onClick={() => setDialog({ kind: 'offer', sendId: m.sendId!, inboxId: m.id })}
              >
                {t('Verifică oferta')}
              </button>
            </div>
          );
        })}

      {rfq.chosen?.split?.length ? (
        <SplitAward
          rfq={rfq}
          canceled={stare === 'canceled'}
          go={go}
          onOrder={(supplierId) => setDialog({ kind: 'order', supplierId })}
          onSupplement={(supplierId) => setDialog({ kind: 'supplement', supplierId })}
          onEdit={() => setDialog({ kind: 'split' })}
        />
      ) : rfq.chosen ? (
        <section className="award sheet" aria-label={t('Furnizorul ales și comanda')}>
          <div className="award-pick">
            <span className="award-k">{t('Ai ales')}</span>
            <b className="award-v">{rfq.chosen.supplier.name}</b>
            <span className="award-s">
              {rfq.chosen.total == null ? '' : t('{0} lei fără TVA · ', fmtNum(rfq.chosen.total))}
              {rfq.chosen.by ? `${rfq.chosen.by.name}, ` : ''}
              {daysAgo(rfq.chosen.at)}
              {rfq.chosen.note ? ` · „${rfq.chosen.note}”` : ''}
            </span>
          </div>
          <div className="award-po">
            {order ? (
              <>
                <span className="award-k">{t('Comanda')}</span>
                <b className="award-v">{order.number}</b>
                <span className="award-s">
                  {t(
                    'emisă {0} · {1} fără TVA',
                    roDateTime(order.issuedAt),
                    poMoney(poTotals(order.data).net, order.data.currency),
                  )}
                </span>
              </>
            ) : (
              <>
                <span className="award-k">{t('Comanda')}</span>
                <b className="award-v muted-v">
                  {waiting
                    ? t('{0} așteaptă aprobare', waiting.number)
                    : lastCanceled
                      ? `${lastCanceled.number} ${lastCanceled.status === 'rejected' ? t('respinsă') : t('anulată')}`
                      : t('Încă nefăcută')}
                </b>
                <span className="award-s">
                  {waiting
                    ? t('Nu pleacă la furnizor până nu e aprobată.')
                    : lastCanceled?.status === 'rejected'
                      ? t('Motivul: {0}. Refă-o pornind de la ea.', lastCanceled.cancelReason || '—')
                      : lastCanceled
                        ? t('Refă-o: pornește de la ce scrisesei pe cea anulată.')
                        : t('Se completează din oferta aleasă, fără reintroducere.')}
                </span>
              </>
            )}
          </div>
          <div className="award-acts">
            {order ? (
              <>
                <button
                  className="btn primary"
                  onClick={() => go({ view: 'order', rfqId: rfq.id, orderId: order.id })}
                >
                  {t('Deschide comanda')}
                </button>
                <button
                  className="btn"
                  title={t('Mai comanzi de la același furnizor, pe același proiect')}
                  onClick={() => setDialog({ kind: 'supplement' })}
                >
                  {t('Suplimentare')}
                </button>
              </>
            ) : waiting ? (
              <button
                className="btn primary"
                onClick={() => go({ view: 'order', rfqId: rfq.id, orderId: waiting.id })}
              >
                {t('Deschide comanda')}
              </button>
            ) : stare === 'canceled' ? null : (
              <button className="btn primary" onClick={() => setDialog({ kind: 'order' })}>
                {lastCanceled ? t('Refă comanda') : t('Fă comanda')}
              </button>
            )}
          </div>
          {supplements.length ? (
            <div className="award-history">
              {t('Suplimentări:')}
              {supplements.map((o, i) => (
                <span key={o.id}>
                  {i ? ', ' : ''}
                  <button
                    className="link"
                    onClick={() => go({ view: 'order', rfqId: rfq.id, orderId: o.id })}
                  >
                    {o.number}
                  </button>{' '}
                  ({poMoney(poTotals(o.data).net, o.data.currency)})
                </span>
              ))}
              <span className="muted"> {t('· fără concurență, nu intră la diferența obținută')}</span>
            </div>
          ) : null}
          {canceledOrders.length ? (
            <div className="award-history">
              {t('Anulate:')}
              {canceledOrders.map((o, i) => (
                <span key={o.id}>
                  {i ? ', ' : ''}
                  <button
                    className="link"
                    onClick={() => go({ view: 'order', rfqId: rfq.id, orderId: o.id })}
                  >
                    {o.number}
                  </button>
                  {o.cancelReason ? ` (${o.cancelReason})` : ''}
                </span>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {rfq.kind === 'reorder' ? (
        <div className="ro-note" role="note">
          <p>{t('Comandă rapidă la prețurile convenite, fără cerere de ofertă.')}</p>
          <button className="btn small" onClick={() => go({ view: 'reorder' })}>
            {t('Comandă rapidă')}
          </button>
        </div>
      ) : rfq.chosen && stare !== 'canceled' ? (
        <div className="ro-note" role="note">
          <p>
            {t(
              'Comanzi des produsele astea? Păstrează prețurile câștigătoare ca prețuri convenite pentru 90 de zile și comandă-le din nou fără o cerere nouă.',
            )}
          </p>
          <button className="btn small" disabled={busy} onClick={() => void keepPrices()}>
            {t('Păstrează prețurile')}
          </button>
        </div>
      ) : null}

      <div className="grid2">
        <section className="sheet">
          <div className="pad" style={{ paddingBottom: 4 }}>
            <div className="section-title">
              <h2>{t('Tabel comparativ')}</h2>
              <button className="btn small" onClick={() => void copyText(cmpTsv(rfq), toast)}>
                {t('Copiază pentru Excel')}
              </button>
            </div>
          </div>
          <ComparisonTable
            rfq={rfq}
            bench={bench.data ?? undefined}
            onAsk={(sendId) => setDialog({ kind: 'ask', sendId })}
            onSplit={stare === 'canceled' ? undefined : () => setDialog({ kind: 'split' })}
          />
        </section>

        <section className="sheet">
          <div className="pad" style={{ paddingBottom: 8 }}>
            <div className="section-title">
              <h2>{t('Furnizori')}</h2>
              <span className="muted" style={{ fontSize: '.9rem' }}>
                {t(
                  '{0}{1} fără răspuns',
                  unsent ? t('{0} de trimis · ', unsent) : '',
                  rfq.sends.length - got - unsent,
                )}
              </span>
            </div>
          </div>
          <div className="sup-list">
            {rfq.sends.map((s) => {
              const st = status(s, rfq);
              const when = s.offer
                ? t(
                    'primită {0}{1}',
                    s.offer.channel === LINK_CHANNEL ? t('prin link ') : '',
                    daysAgo(s.offer.receivedAt),
                  )
                : s.sentAt
                  ? t(
                      'trimisă {0}{1}{2}{3}{4}',
                      s.sentVia === 'procumi' ? t('din Procumi ') : '',
                      daysAgo(s.sentAt),
                      s.followUps ? t(' · {0} follow-up', s.followUps) : '',
                      s.reminderAt ? t(' · memento automat') : '',
                      s.linkOpenedAt ? t(' · a deschis linkul {0}', daysAgo(s.linkOpenedAt)) : '',
                    )
                  : t('mailul n-a plecat încă');
              // Documentele se văd din listă: de multe ori PDF-ul vine înainte
              // ca prețurile să fie trecute în tabel.
              const docs = s.files.length
                ? ` · ${s.files.length} ${s.files.length === 1 ? 'document' : 'documente'}`
                : '';
              return (
                <div className="sup" key={s.id}>
                  <div>
                    <div className="name">
                      {s.supplier.name}
                      {rfqParts(rfq).some((p) => p.supplier.id === s.supplier.id) ? (
                        <span className="pill act" style={{ marginLeft: 8 }}>
                          {t('ales')}
                        </span>
                      ) : null}
                    </div>
                    <div className="who">
                      {s.contact.name ? `${s.contact.name} · ` : ''}
                      {s.contact.email}
                    </div>
                  </div>
                  <div>
                    <span className={`pill ${st.cls}`}>{st.txt}</span>
                    <div className="who" style={{ marginTop: 3 }}>
                      {when}
                      {docs}
                    </div>
                    {s.mailError && !s.offer ? (
                      <div className="mail-error" title={s.mailError}>
                        {t('mailul din Procumi n-a plecat')}
                      </div>
                    ) : null}
                  </div>
                  <div className="acts">
                    {s.offer ? (
                      <button
                        className="btn small"
                        onClick={() => setDialog({ kind: 'offer', sendId: s.id })}
                      >
                        {t('Editează oferta')}
                      </button>
                    ) : (
                      <>
                        {s.sentAt ? (
                          <button
                            className="btn small"
                            onClick={() => setDialog({ kind: 'followUp', sendId: s.id })}
                          >
                            {t('Follow-up')}
                          </button>
                        ) : (
                          <button
                            className="btn small"
                            onClick={() => setDialog({ kind: 'mail', sendId: s.id })}
                          >
                            {t('Trimite mailul')}
                          </button>
                        )}
                        <button
                          className="btn small primary"
                          onClick={() => setDialog({ kind: 'offer', sendId: s.id })}
                        >
                          {t('Adaugă oferta')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <div className="rfq-lower">
        <Comments rfq={rfq} onUpdated={onUpdated} toast={toast} />
        <PlansSection rfq={rfq} onUpdated={onUpdated} toast={toast} />
      </div>

      {dialog?.kind === 'choice' ? (
        <ChoiceDialog
          rfq={rfq}
          bench={bench.data ?? undefined}
          toast={toast}
          onUpdated={onUpdated}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'mail' ? (
        <MailDialog
          rfq={rfq}
          signature={signature}
          toast={toast}
          sendId={dialog.sendId}
          onUpdated={onUpdated}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'offer' && openSend ? (
        <OfferDialog
          rfq={rfq}
          send={openSend}
          canExtract={canExtract}
          inboxId={dialog.inboxId}
          toast={toast}
          onUpdated={onUpdated}
          onClose={() => {
            setDialog(null);
            if (dialog.inboxId) mails.reload();
          }}
        />
      ) : null}
      {dialog?.kind === 'order' &&
      orderFor &&
      !partOrder(rfq, orderFor, 'issued') &&
      !partOrder(rfq, orderFor, 'pending') ? (
        <OrderDialog
          rfq={rfq}
          supplierId={orderFor}
          toast={toast}
          onIssued={(updated) => {
            onUpdated(updated);
            const id = orderFor;
            const issued = partOrder(updated, id, 'issued') ?? partOrder(updated, id, 'pending');
            if (issued) go({ view: 'order', rfqId: updated.id, orderId: issued.id });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'supplement' &&
      rfq.chosen &&
      partOrder(rfq, dialog.supplierId ?? rfq.chosen.supplier.id, 'issued') ? (
        <OrderDialog
          rfq={rfq}
          kind="supplement"
          supplierId={dialog.supplierId}
          toast={toast}
          onIssued={(updated) => {
            onUpdated(updated);
            const id = dialog.supplierId ?? updated.chosen!.supplier.id;
            const added = activeSupplements(updated)
              .filter((o) => o.supplier.id === id)
              .at(-1);
            if (added) go({ view: 'order', rfqId: updated.id, orderId: added.id });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'split' ? (
        <SplitDialog rfq={rfq} toast={toast} onUpdated={onUpdated} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'ask' && ask && askSend ? (
        <AskDialog
          rfq={rfq}
          send={askSend}
          questions={ask.questions}
          signature={signature}
          toast={toast}
          onAsked={onUpdated}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {/* din mailul de dimineață: dacă furnizorul a răspuns între timp, nu mai e de ce */}
      {dialog?.kind === 'followUp' && openSend && !openSend.offer ? (
        <FollowUpDialog
          rfq={rfq}
          send={openSend}
          signature={signature}
          toast={toast}
          onSent={onUpdated}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}
